import { Flex, Typography } from "antd";
import { useUpdateNotificationById } from "hooks/mutations/notification";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { colors } from "shared/theme/color";
import { Notification } from "shared/types";
import { translateMessage } from "shared/utils";
import { notificationContentHtml } from "./notificationText";
import { NotificationIconBadge } from "./NotificationVisuals";
import { ClashNotificationContent } from "./ClashNotificationContent";
import { CustomerBookingContent, customerBookingBody } from "./CustomerBookingContent";
import { textPx } from "shared/constants/zoom";

export const NotificationBlock: React.FC<{ notification: Notification; closeDrawer: () => void }> = ({
  notification,
  closeDrawer,
}) => {
  const clickable = Boolean(notification.link);
  const navigate = useNavigate();
  const { mutate: updateNotificationMutation } = useUpdateNotificationById();
  const [hover, setHover] = useState(false);
  const unseen = !notification.seen;
  const booking = customerBookingBody(notification);

  const handleClick = () => {
    // Set the notification as seen when redirecting to another page
    if (!notification?.seen) {
      updateNotificationMutation(notification.id);
    }
    if (!notification.link) return;
    closeDrawer();
    navigate(notification.link);
  };

  return (
    // Same card as the dashboard rail (white, hairline border, bg-soft on
    // hover; unseen = navy accent bar + dot + bold title) so the drawer and
    // the rail read as one list.
    <Flex
      className={clickable ? "notif-block notif-block--clickable" : "notif-block"}
      onClick={clickable ? handleClick : undefined}
      role={clickable ? "link" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key !== "Enter" && e.key !== " ") return;
              // Space would otherwise also scroll the drawer.
              e.preventDefault();
              handleClick();
            }
          : undefined
      }
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: "relative",
        padding: "12px 14px 12px 16px",
        borderRadius: 12,
        border: `1px solid ${hover ? colors.lineStrong : colors.line}`,
        background: hover ? colors.bgSoft : colors.card,
        cursor: clickable ? "pointer" : undefined,
        transition: "background 150ms ease, border-color 150ms ease",
      }}
      gap={12}
    >
      {unseen && (
        <div
          style={{
            position: "absolute",
            left: 0,
            top: 12,
            bottom: 12,
            width: 3,
            borderRadius: 999,
            background: colors.navy,
          }}
        />
      )}
      <NotificationIconBadge size={30} type={notification.type} />
      <Flex vertical style={{ flex: 1, minWidth: 0 }}>
        <Flex justify={"space-between"} align="flex-start" gap={8}>
          <Typography.Text
            style={{
              flex: 1,
              minWidth: 0,
              fontSize: textPx(13),
              lineHeight: 1.3,
              fontWeight: unseen ? 600 : 500,
              color: colors.ink,
              wordBreak: "break-word",
            }}
          >
            {translateMessage(notification.title)}
          </Typography.Text>
          <span
            style={{
              fontSize: textPx(11),
              color: colors.ink3,
              whiteSpace: "nowrap",
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              flexShrink: 0,
            }}
          >
            {unseen && (
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: 999,
                  background: colors.navy,
                  flexShrink: 0,
                }}
              />
            )}
            {notification.relative_created_at}
          </span>
        </Flex>
        {notification.payload && "groups" in notification.payload ? (
          <ClashNotificationContent payload={notification.payload} />
        ) : booking ? (
          <CustomerBookingContent body={booking} />
        ) : (
          notification.content?.trim() && (
            <div
              dangerouslySetInnerHTML={{ __html: notificationContentHtml(notification) }}
              style={{
                fontSize: textPx(12),
                color: colors.ink2,
                marginTop: 4,
                lineHeight: 1.5,
                wordBreak: "break-word",
                overflowWrap: "break-word",
              }}
            />
          )
        )}
      </Flex>
    </Flex>
  );
};
